import { AtSymbolIcon, BuildingStorefrontIcon, CalendarIcon, ClockIcon, PhoneIcon } from '@heroicons/react/24/solid';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { ConsentPreferredTime, ConsentPreferredTimeLabels } from '../../../../@types/ConsentPreferredTime';
import type { InteractionFormData } from '../../../../@types/contactInteraction';
import type { Owner } from '../../../../@types/Owner';
import type { RootState } from '../../../../store/store';
import MUITable from '../../../common/MUITable/MUITable.module';
import CoproLocalOwned from '../../components/CoproLocalOwned';
import OwnerInfos from '../../components/OwnerInfos';
import { LastContactInfo } from '../../components/PreviousContactList';
import InteractionForm from './ToCallForm';

interface CallbackRequestsTableProps {
  data: Owner[];
  onInteractionSubmit: (ownerId: number, formData: InteractionFormData) => void;
  // API Pagination props
  apiPagination?: boolean;
  totalCount?: number;
  onPageChange?: (page: number, rowsPerPage: number) => void;
  currentPage?: number;
  currentRowsPerPage?: number;
  onRefresh?: () => void;
  // Sorting props
  onSortChange?: (field: string) => void;
  currentSortField?: string;
}

const CallbackRequestsTable: React.FC<CallbackRequestsTableProps> = ({ 
  data, 
  onInteractionSubmit,
  apiPagination = false,
  totalCount,
  onPageChange,
  currentPage = 0,
  currentRowsPerPage = 10,
  onRefresh,
  onSortChange,
  currentSortField = '',
}) => {
  const [selectedOwner, setSelectedOwner] = useState<Owner | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const jwt = useSelector((state: RootState) => state.auth.token);

  const handleCallClick = (owner: Owner) => {
    setSelectedOwner(owner);
    setIsFormOpen(true);
  };

  const handleFormSubmit = (formData: InteractionFormData) => {
    if (selectedOwner && selectedOwner.id) {
      onInteractionSubmit(selectedOwner.id, formData);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleSortChange = (field: string) => {
    if (onSortChange) {
      onSortChange(field);
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) {return 'Non spécifiée';}
    
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(date);
    } catch {
      return dateString;
    }
  };

  const getPreferredTimeLabel = (preferredTime: string | null | undefined): string => {
    if (!preferredTime) {return 'Aucune préférence';}

    if (preferredTime === ConsentPreferredTime.MORNING || preferredTime === ConsentPreferredTime.EVENING || preferredTime === ConsentPreferredTime.NO_PREFERENCE) {
      return ConsentPreferredTimeLabels[preferredTime];
    }

    return preferredTime;
  };

  const getSellOrRentWishLabel = (sellOrRentWish: number | null | undefined): string => {
    if (sellOrRentWish === null || sellOrRentWish === undefined) {return 'Non spécifié';}
    
    switch (sellOrRentWish) {
    case 1: return 'Vente';
    case 2: return 'Location';
    default: return 'Non spécifié';
    }
  };

  const getContactInfo = (owner: Owner) => {
    return <div className="space-y-1">
      {owner.phone && (
        <div className="flex items-center">
          <PhoneIcon className="mr-2 h-4 w-4 shrink-0 text-gray-500" />
          <a href={`tel:${owner.phone}`} className="text-rgpd-blue hover:underline">
            {owner.phone}
          </a>
        </div>
      )}
      {owner.email && (
        <div className="flex items-center">
          <AtSymbolIcon className="mr-2 h-4 w-4 shrink-0 text-gray-500" />
          <a href={`mailto:${owner.email}`} className="text-rgpd-blue hover:underline">
            {owner.email}
          </a>
        </div>
      )}
    </div>;
  };

  const getConsentDetails = (owner: Owner) => {
    // Get consent details from the latest consent
    if (!owner.consents || owner.consents.length === 0) {
      return (
        <div className="rounded-md bg-gray-50 p-3">
          <span className="text-gray-400 italic">Aucune information complétée depuis le formulaire</span>
        </div>
      );
    }

    // Get the most recent consent
    const latestConsent = [...owner.consents].sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )[0];

    return (
      <div className="space-y-1.5 rounded-md bg-gray-50 p-3">
        <div className="flex items-start space-x-2">
          <CalendarIcon className="mt-0.5 h-4 w-4 shrink-0 text-rgpd-blue" />
          <div>
            <div className="text-sm font-medium text-gray-700">Date de rappel</div>
            <div>{formatDate(latestConsent.recallAt)}</div>
          </div>
        </div>
        
        <div className="flex items-start space-x-2">
          <ClockIcon className="mt-0.5 h-4 w-4 shrink-0 text-rgpd-blue" />
          <div>
            <div className="text-sm font-medium text-gray-700">Moment préféré</div>
            <div>{getPreferredTimeLabel(latestConsent.preferredTime)}</div>
          </div>
        </div>
        
        <div className="flex items-start space-x-2">
          <BuildingStorefrontIcon className="mt-0.5 h-4 w-4 shrink-0 text-rgpd-blue" />
          <div>
            <div className="text-sm font-medium text-gray-700">Souhait</div>
            <div>{getSellOrRentWishLabel(latestConsent.sellOrRentWish)}</div>
          </div>
        </div>
        
        {latestConsent.phone && (
          <div className="flex items-start space-x-2">
            <PhoneIcon className="mt-0.5 h-4 w-4 shrink-0 text-rgpd-blue" />
            <div>
              <div className="text-sm font-medium text-gray-700">Téléphone de préférence</div>
              <a href={`tel:${latestConsent.phone}`} className="text-rgpd-blue hover:underline">
                {latestConsent.phone}
              </a>
            </div>
          </div>
        )}
      </div>
    );
  };

  const columns = [
    { title: 'Nom' },
    { title: 'Propriété' },
    { title: 'Coordonnées' },
    { 
      title: 'Dernier contact',
      sortable: true,
      sortField: 'contact.createdAt',
      onSort: () => handleSortChange('contact.createdAt'),
    },
    { 
      title: 'Détails du formulaire',
      sortable: true,
      sortField: 'consent.recallAt',
      onSort: () => handleSortChange('consent.recallAt'),
    },
    { title: 'Actions' },
  ];

  // Transform the data for MUITable
  const tableData = data.map((owner: Owner) => {
    // Create a contact button
    const actionButton = (
      <div className="flex space-x-2">
        <button
          onClick={() => handleCallClick(owner)}
          className="rounded bg-rgpd-blue px-4 py-2 text-white"
        >
          Appeler
        </button>
      </div>
    );

    return {
      'Nom': <OwnerInfos owner={owner} />,
      'Propriété': <CoproLocalOwned owner={owner} />,
      'Coordonnées': getContactInfo(owner),
      'Dernier contact': <LastContactInfo owner={owner} />,
      'Détails du formulaire': getConsentDetails(owner),
      'Actions': actionButton,
    };
  });

  return (
    <div>
      <MUITable
        data={tableData}
        headers={columns}
        disablePagination={false}
        apiPagination={apiPagination}
        totalCount={totalCount}
        onPageChange={onPageChange}
        currentPage={currentPage}
        currentRowsPerPage={currentRowsPerPage}
        currentSortField={currentSortField}
      />

      {isFormOpen && selectedOwner && jwt && (
        <InteractionForm
          owner={selectedOwner}
          isOpen={isFormOpen}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
          jwt={jwt}
        />
      )}
    </div>
  );
};

export default CallbackRequestsTable; 